import React, { useState } from 'react';
import { FileText, Loader2, Send } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Tooltip } from '../../../components/ui/tooltip';
import { useDispatch, useSelector } from 'react-redux';
import HILOChart from './hilo_chartjsx';
import {
  PIPEFITTER_FETCH_PROJECT_SUMMARY_REPORT,
  PIPEFITTER_SEND_EMAIL_PROJECT_SUMMARY,
} from '../../../redux/Pipefitter/types';
import { configurationConstants } from '../constants/configurationConstants';
import { SendReportEmailModal } from './SendReportEmailModal';
import { projectSummaryHelper, hasProjectSummaryChartData } from '../helpers/reportHelpers';
interface PipeCompatibilityProps {
  projectSummaryData: any;
  projectNumber?: string;
  isLoading?: boolean;
  canReport?: boolean;
}

export const PipeCompatibility: React.FC<PipeCompatibilityProps> = ({
  projectSummaryData,
  projectNumber,
  isLoading,
  canReport = true,
}) => {
  const dispatch = useDispatch();
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const project = useSelector((state: any) => state.project?.selectedProject);
  const sequenceStatus = project?.sequenceGenerationStatus;
  const randomSequenceStatus = project?.randomSequenceGenerationStatus;

  const enableDownloadOrSendEmailButton = () => {
    return (
      sequenceStatus === configurationConstants.sequenceGenerationStatus.Completed &&
      randomSequenceStatus === configurationConstants.sequenceGenerationStatus.Completed
    );
  };

  const hasValidData = hasProjectSummaryChartData(projectSummaryData);

  const handleGeneratePDF = async () => {
    if (!projectNumber) return;
    try {
      setIsPdfDownloading(true);
      dispatch({
        type: PIPEFITTER_FETCH_PROJECT_SUMMARY_REPORT,
        payload: { projectNumber },
        cb: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${projectNumber}_Pipe-Compatibility-Report.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          setIsPdfDownloading(false);
        },
      });
    } catch (error) {
      setIsPdfDownloading(false);
      console.error('Error generating PDF:', error);
    }
  };

  const confirmSendEmail = () => {
    setShowEmailModal(true);
  };

  const chartData = projectSummaryHelper(projectSummaryData);

  const bins: string[] = (chartData.options?.xaxis?.categories || []).map(String);
  const rawData =
    bins.length > 0
      ? {
          Omnifit: Object.fromEntries(bins.map((b, i) => [b, chartData.series[0]?.data[i] ?? 0])),
          Optifit: Object.fromEntries(bins.map((b, i) => [b, chartData.series[1]?.data[i] ?? 0])),
          Random: Object.fromEntries(bins.map((b, i) => [b, chartData.series[2]?.data[i] ?? 0])),
        }
      : undefined;

  if (isLoading) {
    return (
      <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" aria-hidden />
        <p className="text-center text-sm text-gray-500">Loading pipe compatibility...</p>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {!hasValidData ? (
              <div className="text-center py-8 text-gray-500">No data available</div>
            ) : (
              <div id="pipe-compatibility-chart">
                <HILOChart rawData={rawData} bins={bins} />
                {/* <ReactApexChart
                  options={chartData.options}
                  series={chartData.series}
                  type="bar"
                  height={290}
                /> */}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {projectNumber && (
        <SendReportEmailModal
          open={showEmailModal}
          onOpenChange={setShowEmailModal}
          projectNumber={projectNumber}
          title="Send Pipe Compatibility Report"
          isSending={isSendingEmail}
          onSend={(emails) => {
            setIsSendingEmail(true);
            dispatch({
              type: PIPEFITTER_SEND_EMAIL_PROJECT_SUMMARY,
              payload: { projectNumber, emails },
              cb: () => {
                setShowEmailModal(false);
                setIsSendingEmail(false);
              },
              error: () => setIsSendingEmail(false),
            });
          }}
        />
      )}
    </>
  );
};
